import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/api";
import { toast } from "sonner";

// V9.20 — Sprint 7C : Cloche de notifications in-app.
// Poll léger toutes les 60s pour rafraîchir le compteur.

// V9.22 — Fallback URL derivation from entity_type + entity_id + action for legacy notifications
function deriveActionUrl(n) {
  if (n.action_url) return n.action_url;
  const et = n.entity_type;
  const eid = n.entity_id;
  const act = n.action || n.type_key || "";

  // SupportCampaign V1
  if (et === "support_campaign" && eid) return `/admin/support-campaigns`;
  if (et === "athlete_campaign" && eid) return `/mes-campagnes`;
  if (et === "athlete_import_batch") return `/admin/athletes/imports`;
  // Affiliations Club/Fédération
  if (et === "entity_link" || act.includes("affiliation")) return `/mes-structures`;
  if (et === "athlete_claim" || act.includes("claim")) return `/mes-fiches`;
  if (et === "entity_manager") return `/mes-structures`;
  // MemoryBook / Livret
  if (et === "memorybook_request") return `/mes-livrets`;
  if (et === "memory_book") return `/mes-livrets`;
  // Fallbacks entity-based
  if (et === "athlete" && eid) return `/athletes/${eid}`;
  if (et === "club" && eid) return `/clubs/${eid}`;
  if (et === "federation" && eid) return `/federations/${eid}`;
  // Fallback global
  return "/mon-espace";
}

export function NotificationBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data?.count || 0);
    } catch (e) {
      // silencieux — on ne veut pas polluer la console
    }
  }, []);

  const fetchList = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data?.items || []);
    } catch (e) {
      // silencieux
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const timer = setInterval(fetchCount, 60000);
    return () => clearInterval(timer);
  }, [fetchCount]);

  useEffect(() => {
    if (open) {
      fetchList();
    }
  }, [open, fetchList]);

  const markRead = async (n) => {
    try {
      await api.post(`/notifications/${n.id}/read`);
      setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, read: true } : it)));
      fetchCount();
    } catch (e) { /* ignore */ }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((it) => ({ ...it, read: true })));
      setUnread(0);
      toast.success("Toutes les notifications ont été marquées comme lues.");
    } catch (e) {
      toast.error("Impossible de marquer les notifications comme lues.");
    }
  };

  const openNotif = async (n) => {
    if (!n.read) await markRead(n);
    const url = deriveActionUrl(n);
    setOpen(false);
    if (url) nav(url);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notif-bell"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-4 min-w-4 rounded-full px-1 text-[10px] leading-none"
              data-testid="notif-badge"
            >
              {unread > 99 ? "99+" : unread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="text-sm font-medium">Notifications</div>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAll} data-testid="notif-mark-all">
              <CheckCheck className="h-3.5 w-3.5 mr-1" /> Tout lu
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-96">
          {items.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground italic">Aucune notification pour l'instant.</div>
          ) : (
            <ul className="divide-y">
              {items.map((n) => (
                <li
                  key={n.id}
                  className={`p-3 text-sm cursor-pointer hover:bg-muted/50 ${!n.read ? "bg-[hsl(var(--aito-lagoon))]/5" : ""}`}
                  onClick={() => openNotif(n)}
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium truncate">{n.title || n.type_key}</div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-[hsl(var(--aito-lagoon))] mt-1.5 flex-shrink-0" />}
                  </div>
                  {(n.message || n.body) && (
                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message || n.body}</div>
                  )}
                  <div className="text-[10px] text-muted-foreground mt-1 font-mono">
                    {new Date(n.created_at).toLocaleString("fr-FR")}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
